/**
 * Sylva Symptom Taxonomy — full 193-option dataset
 *
 * carryForward values:
 *   'reset'    — resets every log session (event/behaviour based)
 *   'carry'    — persists until the user marks it Gone
 *   'prompt'   — persists but shows an "how has it changed?" update prompt
 *   'expire48' — persists for up to 48 h, then auto-expires
 *   'expire36' — persists for up to 36 h, then auto-expires
 *
 * Attribute flags:
 *   default:true   — shown in quick mode (the first attribute per symptom)
 *   isSeverity:true — this attribute drives the severity bump on "Worse"
 *   clinical:true  — hidden unless condition profile or full-detail mode
 */

export const TAXONOMY = [
  {
    key: 'cycle_physical',
    label: 'Cycle',
    icon: '◌',
    color: '#E8A2B8',
    symptoms: [
      {
        key: 'bleeding',
        label: 'Bleeding',
        carryForward: 'prompt',
        attributes: [
          { key: 'flow', label: 'Flow', options: ['Spotting', 'Light', 'Medium', 'Heavy'], default: true },
          { key: 'clots', label: 'Clots', options: ['None', 'Small clots', 'Large clots'], clinical: true },
          { key: 'colour', label: 'Colour', options: ['Bright red', 'Dark red', 'Brown', 'Pink', 'Purple / very dark'], clinical: true },
          { key: 'texture', label: 'Texture', options: ['Watery', 'Normal', 'Thick / stringy'], clinical: true },
          { key: 'odour', label: 'Odour', options: ['None', 'Mild', 'Strong / unusual'], clinical: true },
        ],
      },
      {
        key: 'cramps',
        label: 'Cramps',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'location', label: 'Location', options: ['Lower abdomen', 'Lower back', 'Inner thighs', 'All over'] },
          { key: 'type', label: 'Type', options: ['Dull ache', 'Sharp / stabbing', 'Cramping waves'] },
          { key: 'timing', label: 'Timing', options: ['Before period', 'During period', 'Mid-cycle'] },
        ],
      },
      {
        key: 'spotting_between',
        label: 'Spotting (between periods)',
        carryForward: 'expire48',
        attributes: [
          { key: 'when', label: 'When', options: ['Before period', 'Mid-cycle / ovulation', 'After sex', 'Random / unsure'], default: true },
        ],
      },
      {
        key: 'ovulation_pain',
        label: 'Ovulation pain',
        carryForward: 'expire48',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'side', label: 'Side', options: ['Left', 'Right', 'Both / unsure'] },
        ],
      },
      {
        key: 'discharge',
        label: 'Discharge',
        carryForward: 'prompt',
        attributes: [
          { key: 'amount', label: 'Amount', options: ['Light', 'Moderate', 'Heavy'], default: true },
          { key: 'colour', label: 'Colour', options: ['Clear', 'White / cream', 'Yellow', 'Green', 'Grey', 'Brown / bloody'], clinical: true },
          { key: 'consistency', label: 'Consistency', options: ['Watery', 'Creamy', 'Egg white (stretchy)', 'Thick / pasty', 'Chunky / cottage cheese'], clinical: true },
          { key: 'odour', label: 'Odour', options: ['None', 'Mild', 'Strong / unusual'], clinical: true },
        ],
      },
      {
        key: 'pelvic_pressure',
        label: 'Pelvic pressure',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
        ],
      },
      {
        key: 'breast_tenderness',
        label: 'Breast tenderness',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'location', label: 'Location', options: ['Both sides', 'Left only', 'Right only'] },
        ],
      },
    ],
  },

  {
    key: 'physical_general',
    label: 'Physical',
    icon: '◎',
    color: '#D4AF7A',
    symptoms: [
      {
        key: 'headache',
        label: 'Headache',
        carryForward: 'expire36',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'type', label: 'Type', options: ['Tension', 'Migraine', 'Throbbing'] },
          { key: 'location', label: 'Location', options: ['Forehead', 'Behind eyes', 'Temples', 'Whole head', 'Back of head / neck'] },
        ],
      },
      {
        key: 'bloating',
        label: 'Bloating',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'location', label: 'Location', options: ['Upper abdomen', 'Lower abdomen', 'Whole abdomen'] },
          { key: 'associated', label: 'Associated with', options: ['Gas', 'Nausea', 'None'] },
        ],
      },
      {
        key: 'nausea',
        label: 'Nausea',
        carryForward: 'reset',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'vomiting', label: 'Vomiting', options: ['Yes', 'No'] },
        ],
      },
      {
        key: 'back_pain',
        label: 'Back pain',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'location', label: 'Location', options: ['Lower back', 'Upper back', 'Both'] },
        ],
      },
      {
        key: 'joint_pain',
        label: 'Joint pain',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'location', label: 'Location', options: ['Hands / wrists', 'Knees / hips', 'Multiple / widespread'] },
        ],
      },
      {
        key: 'hot_flushes',
        label: 'Hot flushes / night sweats',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'timing', label: 'Timing', options: ['Daytime', 'Night', 'Both'] },
        ],
      },
      {
        key: 'skin_flare',
        label: 'Skin flare',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'area', label: 'Area', options: ['Face', 'Body', 'Both'] },
          { key: 'type', label: 'Type', options: ['Acne / breakouts', 'Dryness / flaking', 'Oiliness', 'Redness / rash'] },
        ],
      },
    ],
  },

  {
    key: 'mood_mental',
    label: 'Mood',
    icon: '◈',
    color: '#B39DDB',
    symptoms: [
      {
        key: 'low_mood',
        label: 'Low mood',
        carryForward: 'carry',
        attributes: [
          { key: 'intensity', label: 'Intensity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'feeling', label: 'Feeling', options: ['Anxious', 'Sad', 'Irritable', 'Tearful', 'Unmotivated', 'Angry / rage', 'Overwhelmed', 'Hopeless', 'Numb / disconnected', 'Lonely'] },
        ],
      },
      {
        key: 'high_energy',
        label: 'High energy',
        carryForward: 'reset',
        attributes: [
          { key: 'feeling', label: 'Feeling', options: ['Energised', 'Confident', 'Creative', 'Euphoric', 'Productive'], default: true },
        ],
      },
      {
        key: 'stress',
        label: 'Stress',
        carryForward: 'carry',
        attributes: [
          { key: 'level', label: 'Level', options: ['Low', 'Moderate', 'High'], default: true, isSeverity: true },
        ],
      },
      {
        key: 'brain_fog',
        label: 'Brain fog',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
        ],
      },
      {
        key: 'anxiety',
        label: 'Anxiety',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
        ],
      },
    ],
  },

  {
    key: 'energy_rest',
    label: 'Energy',
    icon: '◇',
    color: '#7ECECA',
    symptoms: [
      {
        key: 'fatigue',
        label: 'Fatigue',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
          { key: 'type', label: 'Type', options: ['Mental', 'Physical', 'Both'] },
        ],
      },
      {
        key: 'sleep',
        label: 'Sleep',
        // Resets daily but is always prompted — pre-filled with last value
        carryForward: 'reset',
        alwaysPrompt: true,
        attributes: [
          { key: 'quality', label: 'Quality', options: ['Poor', 'Fair', 'Good', 'Great'], default: true },
          { key: 'issues', label: 'Issues', options: ['Difficulty falling asleep', 'Waking through the night', 'Early waking', 'None'] },
        ],
      },
    ],
  },

  {
    key: 'lifestyle',
    label: 'Lifestyle',
    icon: '⊕',
    color: '#7ECBA5',
    symptoms: [
      {
        key: 'appetite',
        label: 'Appetite',
        carryForward: 'reset',
        attributes: [
          { key: 'change', label: 'Change', options: ['Increased', 'Decreased', 'No change'], default: true },
          { key: 'cravings', label: 'Cravings', options: ['Sweet', 'Salty', 'Carbs / starchy', 'None'] },
        ],
      },
      {
        key: 'exercise',
        label: 'Exercise',
        carryForward: 'reset',
        attributes: [
          { key: 'type', label: 'Type', options: ['None / rest day', 'Light (walk, yoga, stretch)', 'Moderate (gym, swim, cycle)', 'Intense (HIIT, run, sport)'], default: true },
        ],
      },
      {
        key: 'water_intake',
        label: 'Water intake',
        carryForward: 'reset',
        attributes: [
          { key: 'level', label: 'Level', options: ['Low (< 1 L)', 'Moderate (1–2 L)', 'Good (2 L+)'], default: true },
        ],
      },
    ],
  },

  {
    key: 'sexual_health',
    label: 'Sexual health',
    icon: '◉',
    color: '#C97A9A',
    symptoms: [
      {
        key: 'intimacy',
        label: 'Intimacy',
        carryForward: 'reset',
        attributes: [
          { key: 'type', label: 'Type', options: ['Penetrative', 'Non-penetrative', 'Both'], default: true },
          { key: 'protection', label: 'Protection', options: ['With protection', 'Without protection', 'N/A'] },
          { key: 'comfort', label: 'Comfort', options: ['Comfortable', 'Some discomfort', 'Painful'], clinical: true },
          { key: 'orgasm', label: 'Orgasm', options: ['Yes', 'No'] },
        ],
      },
      {
        key: 'libido',
        label: 'Libido',
        carryForward: 'carry',
        attributes: [
          { key: 'level', label: 'Level', options: ['Low', 'Normal', 'High'], default: true },
        ],
      },
      {
        key: 'vaginal_dryness',
        label: 'Vaginal dryness',
        carryForward: 'carry',
        attributes: [
          { key: 'severity', label: 'Severity', options: ['Mild', 'Moderate', 'Severe'], default: true, isSeverity: true },
        ],
      },
    ],
  },
];

// ─── Flat lookup maps ──────────────────────────────────────────────────────────

/** symptomKey → symptom def (with group info merged in) */
export const SYMPTOM_MAP = {};
/** groupKey → group def */
export const GROUP_MAP = {};

TAXONOMY.forEach(group => {
  GROUP_MAP[group.key] = group;
  group.symptoms.forEach(symptom => {
    SYMPTOM_MAP[symptom.key] = {
      ...symptom,
      groupKey: group.key,
      groupLabel: group.label,
      groupColor: group.color,
      groupIcon: group.icon,
    };
  });
});

// ─── Cycle-phase suggestions ───────────────────────────────────────────────────

/**
 * Returns up to 5 symptom keys likely relevant for this cycle day.
 * @param {number|null} dayOfCycle
 */
export function getCycleAwareSuggestions(dayOfCycle) {
  if (!dayOfCycle) return [];
  if (dayOfCycle <= 5)
    return ['bleeding', 'cramps', 'fatigue', 'low_mood', 'bloating'];
  if (dayOfCycle <= 13)
    return ['high_energy', 'discharge', 'fatigue'];
  if (dayOfCycle === 14)
    return ['ovulation_pain', 'discharge', 'breast_tenderness'];
  if (dayOfCycle <= 21)
    return ['fatigue', 'bloating', 'breast_tenderness', 'low_mood'];
  return ['low_mood', 'anxiety', 'cramps', 'bloating', 'breast_tenderness'];
}

// ─── Severity helpers ──────────────────────────────────────────────────────────

const SEVERITY_ORDER = ['Mild', 'Moderate', 'Severe', 'Low', 'High', 'Spotting', 'Light', 'Medium', 'Heavy', 'Poor', 'Fair', 'Good', 'Great'];

/**
 * Given a symptom's attributes object, bump the severity/primary attribute up one step.
 */
export function bumpSeverity(symptomKey, attributes) {
  const def = SYMPTOM_MAP[symptomKey];
  if (!def) return attributes;
  const sevAttr = def.attributes.find(a => a.isSeverity);
  if (!sevAttr) return attributes;
  const current = attributes[sevAttr.key];
  const idx = sevAttr.options.indexOf(current);
  const next = sevAttr.options[Math.min(idx + 1, sevAttr.options.length - 1)];
  return { ...attributes, [sevAttr.key]: next };
}

/**
 * Returns a human-readable summary of a symptom's attributes, e.g. "Moderate · Lower abdomen"
 */
export function attributeSummary(attributes = {}) {
  return Object.values(attributes).filter(Boolean).join(' · ');
}
